//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import {
  CampaignTypeFilter,
  MediaFilterParamKey,
} from '@apis/content-library/contentLibraryApiModel';
import { useGetClientRetailersQuery } from '@campaigns/components/campaign-requests/sampling-campaigns/SamplingCampaignsQueries';
import { getIsCreatorCampaignsEnabled } from '@campaigns/components/campaigns-overview/selectors/getIsCreatorCampaignsEnabled';
import HR from '@components/atoms/hr/HR';
import { CenteredSpinner } from '@components/atoms/spinner/Spinner';
import { CheckboxSetFilter } from '@components/media-filters/CheckboxSetFilter';
import { CollapsibleSection } from '@components/media-filters/CollapsibleSection';
import {
  FilterOption,
  FilterSection,
  FilterSectionTitle,
  FilterValue,
} from '@components/media-filters/filterSectionModels';
import { MediaLibraryTrackingIds } from '@media-library/constants/mediaLibraryTrackingIds';
import { getClientId } from '@redux/selectors/getClientId';

export const getDefaultCampaignTypeFilterSection = (
  isCreatorCampaignsEnabled: boolean,
): FilterSection<string> => {
  return {
    options: [
      {
        isSelected: false,
        label: 'Not from Campaigns',
        value: CampaignTypeFilter.NONE,
        trackingId: MediaLibraryTrackingIds.CAMPAIGNS_NONE_CHECKBOX,
      },
      ...(isCreatorCampaignsEnabled
        ? [
            {
              isSelected: false,
              label: 'Creator campaigns',
              value: CampaignTypeFilter.CREATOR,
              trackingId: MediaLibraryTrackingIds.CAMPAIGNS_CREATOR_CHECKBOX,
            },
          ]
        : []),
      {
        isSelected: false,
        label: 'Sampling Campaigns',
        value: CampaignTypeFilter.SAMPLING,
        trackingId: MediaLibraryTrackingIds.CAMPAIGNS_SAMPLING_CHECKBOX,
      },
    ],
    paramKey: 'campaignTypes',
  };
};

export const DEFAULT_EXCLUDE_TARGETED_RETAILER_FILTER_SECTION: FilterSection<boolean> =
  {
    options: [
      {
        isSelected: false,
        label: 'Brand Campaigns',
        value: true,
        trackingId: MediaLibraryTrackingIds.CAMPAIGNS_BRAND_CHECKBOX,
      },
    ],
    paramKey: 'excludeTargetedRetailerCampaignItems',
  };

export const DEFAULT_EXCLUDE_NON_TARGETED_RETAILER_FILTER_SECTION: FilterSection<boolean> =
  {
    options: [
      {
        isSelected: false,
        label: 'My Campaigns',
        value: true,
        trackingId: MediaLibraryTrackingIds.CAMPAIGNS_RETAILER_CHECKBOX,
      },
    ],
    paramKey: 'excludeNonTargetedRetailerCampaignItems',
  };

const getUnselectedCampaignTypeSection = (
  section: FilterSection<string>,
  campaignTypes: CampaignTypeFilter[],
): FilterSection<string> => {
  return {
    ...section,
    options: section.options.map((option) => ({
      ...option,
      isSelected: campaignTypes.includes(option.value as CampaignTypeFilter)
        ? false
        : option.isSelected,
    })),
  };
};

interface RetailerFilterConfig {
  isSelected?: boolean;
  section: FilterSection<boolean>;
  value?: boolean;
}

const getUpdatedRetailerFilterSection = (
  retailerConfig: RetailerFilterConfig,
): [[MediaFilterParamKey, FilterSection<boolean>]] => {
  return [
    [
      retailerConfig.section.paramKey,
      {
        ...retailerConfig.section,
        options: retailerConfig.section.options.map((option) => ({
          ...option,
          isSelected: retailerConfig.isSelected ?? option.isSelected,
          value: retailerConfig.value ?? option.value,
        })),
      },
    ],
  ];
};

const isBrandOwnedSelected = (
  excludeTargetedRetailerSection: FilterSection<FilterValue> | undefined,
): boolean => {
  return (
    excludeTargetedRetailerSection?.options.find((option) => option.isSelected)
      ?.isSelected ?? false
  );
};

const isRetailerSelected = (
  excludeNonTargetedRetailerSection: FilterSection<FilterValue> | undefined,
): boolean => {
  return (
    excludeNonTargetedRetailerSection?.options.find(
      (option) => option.isSelected,
    )?.isSelected ?? false
  );
};

const isBothRetailerTypesSelected = (
  excludeTargetedRetailerSection: FilterSection<FilterValue> | undefined,
  excludeNonTargetedRetailerSection: FilterSection<FilterValue> | undefined,
): boolean =>
  isBrandOwnedSelected(excludeTargetedRetailerSection) &&
  isRetailerSelected(excludeNonTargetedRetailerSection);

export interface CampaignsFilterProps {
  excludeNonTargetedRetailerSection: FilterSection<boolean> | undefined;
  excludeTargetedRetailerSection: FilterSection<boolean> | undefined;
  isDisabled?: boolean;
  isInitiallyCollapsed?: boolean;
  onChange: (
    sections: Map<MediaFilterParamKey, FilterSection<FilterValue>>,
  ) => void;
  section: FilterSection<string> | undefined;
}

export const CampaignsFilter = ({
  excludeNonTargetedRetailerSection = DEFAULT_EXCLUDE_NON_TARGETED_RETAILER_FILTER_SECTION,
  excludeTargetedRetailerSection = DEFAULT_EXCLUDE_TARGETED_RETAILER_FILTER_SECTION,
  isDisabled,
  isInitiallyCollapsed,
  onChange,
  section,
}: CampaignsFilterProps) => {
  const isCreatorCampaignsEnabled = useSelector(getIsCreatorCampaignsEnabled);

  const clientId = useSelector(getClientId);
  const { data: isRetailer, isLoading } = useGetClientRetailersQuery((data) =>
    data.results.some((retailer) => retailer.clientId === clientId),
  );

  const campaignTypeSection =
    section ?? getDefaultCampaignTypeFilterSection(isCreatorCampaignsEnabled);

  if (isLoading) {
    return <CenteredSpinner />;
  }

  return (
    <>
      <StyledCollapsibleSection
        isCollapsed={isInitiallyCollapsed}
        title={FilterSectionTitle[campaignTypeSection.paramKey]}
      >
        <CheckboxSetFilter
          isDisabled={isDisabled}
          onChange={(sections) => {
            const isSamplingSelected = sections
              .get(campaignTypeSection.paramKey)
              ?.options.some(
                (option) =>
                  option.value === CampaignTypeFilter.SAMPLING &&
                  option.isSelected,
              );
            const updatedSections = [
              ...getUpdatedRetailerFilterSection({
                isSelected: isSamplingSelected,
                section: excludeNonTargetedRetailerSection,
                value: !isSamplingSelected,
              }),
              ...getUpdatedRetailerFilterSection({
                isSelected: isSamplingSelected,
                section: excludeTargetedRetailerSection,
                value: !isSamplingSelected,
              }),
            ];
            onChange(
              new Map<MediaFilterParamKey, FilterSection<FilterValue>>([
                ...Array.from(sections),
                ...updatedSections,
              ]),
            );
          }}
          section={campaignTypeSection}
        />
        {isRetailer && (
          <SubsectionContainer>
            {/* My Campaigns Filter */}
            <CheckboxSetFilter
              isDisabled={isDisabled}
              onChange={(sections) => {
                const updatedCampaignSection: [
                  MediaFilterParamKey,
                  FilterSection<FilterValue>,
                ] = [
                  campaignTypeSection.paramKey,
                  getUnselectedCampaignTypeSection(campaignTypeSection, [
                    CampaignTypeFilter.SAMPLING,
                  ]),
                ];

                const updatedSections = [
                  ...getUpdatedRetailerFilterSection({
                    isSelected: isRetailerSelected(
                      sections.get(excludeNonTargetedRetailerSection.paramKey),
                    ),
                    section: excludeNonTargetedRetailerSection,
                    value: true,
                  }),
                  ...getUpdatedRetailerFilterSection({
                    isSelected: isBothRetailerTypesSelected(
                      excludeTargetedRetailerSection,
                      sections.get(excludeNonTargetedRetailerSection.paramKey),
                    )
                      ? false
                      : undefined,
                    section: excludeTargetedRetailerSection,
                    value: true,
                  }),
                ];
                onChange(new Map([...updatedSections, updatedCampaignSection]));
              }}
              section={excludeNonTargetedRetailerSection}
            />
            {/* Brand Campaigns Filter */}
            <CheckboxSetFilter
              isDisabled={isDisabled}
              onChange={(sections) => {
                const updatedCampaignSection: [
                  MediaFilterParamKey,
                  FilterSection<FilterValue>,
                ] = [
                  campaignTypeSection.paramKey,
                  getUnselectedCampaignTypeSection(campaignTypeSection, [
                    CampaignTypeFilter.SAMPLING,
                  ]),
                ];

                const updatedSections = [
                  ...getUpdatedRetailerFilterSection({
                    isSelected: isBrandOwnedSelected(
                      sections.get(excludeTargetedRetailerSection.paramKey),
                    ),
                    section: excludeTargetedRetailerSection,
                    value: true,
                  }),
                  ...getUpdatedRetailerFilterSection({
                    isSelected: isBothRetailerTypesSelected(
                      sections.get(excludeTargetedRetailerSection.paramKey),
                      excludeNonTargetedRetailerSection,
                    )
                      ? false
                      : undefined,
                    section: excludeNonTargetedRetailerSection,
                    value: true,
                  }),
                ];
                onChange(new Map([...updatedSections, updatedCampaignSection]));
              }}
              section={excludeTargetedRetailerSection}
            />
          </SubsectionContainer>
        )}
      </StyledCollapsibleSection>
      <HR />
    </>
  );
};

const SubsectionContainer = styled.div`
  padding-left: ${({ theme }) => theme.spacings[4]};
`;

const StyledCollapsibleSection = styled(CollapsibleSection)`
  & div {
    gap: ${({ theme }) => theme.spacings[1]};
  }
`;
